import { cva } from 'class-variance-authority';

/**
 * Chat input class recipes.
 *
 * Like `TextInput.recipe`, these contain ZERO `dark:` variants — colour comes entirely from the
 * `--ds-chat-input-*` slots that `.ds-chat-input` sets in `components/styles/design-system.css`.
 * One family, written by that one class, per the invariant stated beside `.ds-input` there: this
 * component reads no other component's slots, so nothing tuned for a text input can move it.
 *
 * Every state selector reads Base UI's `Field.Root` attributes through the `group/ti` marker
 * (`data-focused`, `data-invalid`, `data-disabled`), so there is no React state behind the colour
 * changes and the states are reachable from a story or a test by rendering rather than simulating.
 * The one exception is the SHAPE, which cannot be observed from the DOM — see `data-multiline` below.
 *
 * The marker is `/ti` — the SAME name `TextInput` uses — and that is deliberate rather than copied.
 * This component reuses `textInputHelperRecipe` for its helper text, and that recipe turns the text
 * red with `group-data-[invalid]/ti:`. A private `/ci` marker compiles, renders and reads correctly
 * in every other state, and silently leaves the error description in the resting colour. Caught by
 * diffing the dark Error cells against node 1137:29421, which no unit test would have noticed.
 *
 * So the GROUP NAME is shared contract even though the slot family is not. The two are independent:
 * slots are written per component to stop one component's colours moving another's, while the group
 * name is how a shared recipe finds the field root it is rendered inside. The same bug arrived a
 * second time from the other direction when the families were split and this component's helper was
 * still asking for the pill's shape — see the `chat` variant in `TextInput.recipe`.
 *
 * The state selectors are written to be MUTUALLY EXCLUSIVE rather than relying on utility sort
 * order, the same discipline the button and text-input recipes use. Precedence, highest first:
 *
 *   disabled → invalid → focused → hover → resting
 *
 * ── Two shapes, one DOM ────────────────────────────────────────────────────────────────────────
 *
 * Figma's `Multi-line` axis (node 1137:27674) draws two shapes: a 56px pill with the send button
 * inline, and a 24px-radius box with the button below the text. `ChatInput` grows from one into the
 * other as the value wraps, so both have to be reachable without reordering or remounting anything
 * — a remount would drop focus and the caret mid-sentence.
 *
 * They are therefore the SAME three children (control, send button, helper) in the same order, and
 * only the box's `flex-direction` / `align-items` / `border-radius` change. `items-end` in the
 * multi-line shape is what pushes the button to the right edge under the text; `items-center` in
 * the pill is what centres the 36px button in the 56px row.
 *
 * ── Where the padding lives ────────────────────────────────────────────────────────────────────
 *
 * On the BOX here, not the control — the opposite of `TextInput`, and deliberately so.
 *
 * `TextInput` pays its padding from inside the input so the element fills the box and every pixel
 * is a click target. That trick needs the control to span the whole box, which it cannot do here:
 * the send button occupies the right-hand side of the pill and the bottom of the multi-line box, so
 * the control is a flex sibling rather than the full surface. Paying the inset from the box instead
 * keeps Figma's own numbers — 20px leading, 10px trailing and vertical, node 1137:28173 — legible
 * against the file.
 *
 * They are spelled as arbitrary px values rather than named steps, because a named step is a
 * multiple of 4px — the step whose number is 20 is 80px, not 20px — and the scale has none at
 * 20px. The numbers above are given in prose rather than in utility form for the same reason:
 * `noOffScaleSpacing` matches file TEXT, so a comment naming an off-scale step is itself an
 * offender. This paragraph was rewritten twice on that account.
 *
 * The hit area is then recovered explicitly rather than geometrically: `ChatInput` focuses the
 * control on a mousedown anywhere in the box that is not the button. That is strictly better than
 * the padding trick — it also covers the gap between the control and the button, which no amount of
 * padding on the control could reach.
 */

/**
 * Marker + layout for `Field.Root`.
 *
 * The `.ds-chat-input` slot class lives HERE rather than on the bordered box, for the reason
 * `textInputRootRecipe` gives: custom properties cascade, so the helper text — which is a SIBLING of
 * the box — would otherwise fall outside their scope and silently resolve to inherited colour.
 */
export const chatInputRootRecipe = cva(['group/ti ds-chat-input', 'relative flex w-full flex-col gap-[4px]']);

/**
 * The bordered box.
 *
 * ── Why the border is always there ─────────────────────────────────────────────────────────────
 *
 * EVERY cell has a 1px stroke — `--stroke-control` when enabled and when disabled, `border/focus`
 * when focused, `border/destructive` in error (nodes 1137:28173, 1137:45012). The fill is what
 * matches this control to the panel it sits on, and the border is what identifies it as a control;
 * on the broadcast panel the two roles resolve to the same #17191c, so the border is the only thing
 * separating the composer from the surface behind it.
 *
 * So the border is present in every state at a constant WIDTH and only its colour changes. Width is
 * layout: growing a border on focus moves the padding box, and the value text would visibly jump a
 * pixel on every focus and on every tab through the panel.
 *
 * ── Why there is no focus ring ─────────────────────────────────────────────────────────────────
 *
 * `TextInput` and `TextInputPill` both draw a halo on focus. This set does not: node 1137:45012 is
 * a 1px `border/focus` and nothing else. Reproduced as authored — a focus border against the
 * secondary surface carries the state on its own for WCAG 1.4.11, with no ring to help it.
 */
export const chatInputBoxRecipe = cva(
  [
    'relative flex w-full gap-[4px]',
    'min-h-[56px] pl-[20px] pr-[10px] py-[10px]',
    'border-solid border-[length:var(--ds-chat-input-border-width)] border-[var(--ds-chat-input-border)]',
    'bg-[var(--ds-chat-input-bg)]',
    'cursor-text',
    'transition-[border-color,background-color] duration-150 motion-reduce:transition-none',

    // Hover is excluded from every other state so it can never fight them.
    'group-[:hover:not([data-disabled]):not([data-focused]):not([data-invalid])]/ti:border-[var(--ds-chat-input-border-hover)]',

    'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:border-[var(--ds-chat-input-border-focus)]',
    'group-[[data-invalid]:not([data-disabled])]/ti:border-[var(--ds-chat-input-border-invalid)]',
    'group-data-[disabled]/ti:border-[var(--ds-chat-input-border-disabled)]',
    'group-data-[disabled]/ti:bg-[var(--ds-chat-input-bg-disabled)]',
    'group-data-[disabled]/ti:cursor-not-allowed',
  ],
  {
    variants: {
      /**
       * Figma's `Multi-line` axis.
       *
       * `rounded-full` on the pill rather than the `rounded-[56px]` the file authors: unlike
       * `TextInputPill`, this box GROWS, so a fixed radius equal to the resting height would stop
       * agreeing with the shape the moment a second line appeared. The pill is only ever drawn at
       * its resting height, where the two are identical.
       */
      multiline: {
        false: 'flex-row items-center rounded-full',
        true: 'flex-col items-end rounded-[24px]',
      },
    },
    defaultVariants: { multiline: false },
  }
);

/**
 * The control — a `<textarea>`, which is the whole reason this component exists rather than a
 * variant of `TextInput`: DS `Field.Control` renders an `<input>`, and an `<input>` neither holds a
 * newline nor WRAPS. The wrapping is the part that survives `allowNewlines={false}`, so a consumer
 * that forbids newline entry still needs this element rather than an input.
 *
 * Preflight is deliberately absent from the token layer, so the browser's own textarea styling is
 * still in play and every reset is spelled out. This list is `TextInput`'s `controlBase` minus the
 * `<input>`-only bits (`::-ms-reveal`, `::-webkit-search-cancel-button` — a textarea has neither)
 * and plus the two a textarea needs: `resize-none`, because the design has no drag handle and one
 * would let the user break the pill; and `overflow-y-auto`, which is what `max-h` scrolls against.
 */
export const chatInputControlRecipe = cva(
  [
    'm-0 block w-full appearance-none resize-none border-0 bg-transparent',
    // `text-body-md` rather than an arbitrary size plus a `leading-` utility, which is what this
    // was authored with. The rendered output is identical — the token is 14px/20px/400 — but the
    // scale utility is the only spelling `noHardcodedTypography` accepts, so one Figma text style
    // has one representation in code. Do not restate the old form even in a comment: that guard
    // matches file TEXT, so a comment naming a size is itself an offender.
    //
    // The utility's 20px line height is load-bearing here — every height below counts 20px lines.
    'font-sans text-body-md text-[var(--ds-chat-input-value)]',
    'outline-none',
    'placeholder:text-[var(--ds-chat-input-placeholder)]',
    'disabled:cursor-not-allowed disabled:text-[var(--ds-chat-input-value-disabled)]',
    'cursor-text',

    /**
     * 36px tall at rest, matching the send button beside it, which is how the pill measures 56px
     * (10 + 36 + 10) — and, in the multi-line shape, how the empty cell measures 96px
     * (10 + 36 + 4 + 36 + 10, node 1137:45341).
     *
     * The cap is Figma's 80px of text (four 20px lines) PLUS the 16px of padding this element pays
     * itself, because `index.css` sets `box-sizing: border-box` globally — a flat 80px here would
     * cap the text at three lines and a bit.
     */
    'min-h-[36px] max-h-[96px] py-[8px]',
    'overflow-y-auto',

    /**
     * The 6px pill scrollbar the design draws once the text overflows (node 1155:607).
     *
     * Both syntaxes are needed and neither is redundant: `scrollbar-width`/`scrollbar-color` is the
     * standard property that Firefox implements, and the `::-webkit-scrollbar` pseudo-elements are
     * what Chrome and Safari read. A browser that supports the standard property ignores the
     * pseudo-elements, so they cannot disagree.
     */
    '[scrollbar-width:thin] [scrollbar-color:var(--ds-chat-input-scrollbar)_transparent]',
    '[&::-webkit-scrollbar]:w-[6px]',
    '[&::-webkit-scrollbar-track]:bg-transparent',
    '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--ds-chat-input-scrollbar)]',
  ],
  {
    variants: {
      /**
       * `flex-1` with `min-w-0` in the pill so a long unbroken word shrinks the control rather than
       * pushing the send button out of the box; full width in the multi-line shape, where the button
       * is on its own row and there is nothing to share with.
       */
      multiline: {
        false: 'min-w-0 flex-1',
        true: 'w-full',
      },
    },
    defaultVariants: { multiline: false },
  }
);

/**
 * The send button — 48×36, not a `Button` cell.
 *
 * Deliberately NOT built on the DS `Button`, and the reason is three divergences rather than a
 * shortcut: Figma draws it 48 wide against 36 tall where `iconOnly` buttons are square
 * (`size-9` at `small`, `Button.recipe`), its glyph is 20px against `--ds-btn-icon-size`, and its
 * disabled cell uses its own colours rather than `.ds-btn-primary-default`'s. Three differences is
 * a different component, not a variant — flagged for design on HNS-264.
 *
 * `shrink-0` matters in the pill: without it a long value squeezes the button into an ellipse.
 */
export const chatInputSendRecipe = cva([
  'inline-flex shrink-0 items-center justify-center',
  'h-[36px] w-[48px] rounded-full',
  'm-0 cursor-pointer appearance-none border-0 p-0',
  'bg-[var(--ds-chat-input-send-bg)] text-[var(--ds-chat-input-send-fg)]',
  'transition-[background-color,color] duration-150 motion-reduce:transition-none',
  // `[&:not(:disabled):hover]` rather than `enabled:hover`, matching `buttonRecipe`'s form — the
  // one difference being that this is a native `<button disabled>`, so the selector is `:disabled`
  // where Base UI's button carries `data-disabled`. An arbitrary variant also cannot depend on
  // whether `enabled:` happens to be registered.
  '[&:not(:disabled):hover]:bg-[var(--ds-chat-input-send-bg-hover)]',
  'disabled:cursor-not-allowed',
  'disabled:bg-[var(--ds-chat-input-send-bg-disabled)] disabled:text-[var(--ds-chat-input-send-fg-disabled)]',
  /**
   * The button's own focus ring, which the box's border cannot stand in for — tabbing from the
   * textarea to the button keeps `data-focused` on the field either way, so without this the
   * keyboard user gets no indication that focus moved.
   */
  'outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
  'focus-visible:outline-[var(--ds-chat-input-border-focus)]',
]);
